import React, { useState } from "react";

import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  EyeIcon,
  InfoIcon,
  Loader2Icon,
  Lock,
  Share2Icon,
} from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "js/components/ui/button";
import { StatusBadge } from "js/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

interface DownloadItemProps {
  title: string;
  isCompleted: boolean;
  /** Download action in flight — spins whichever button triggers the download. */
  isDownloading?: boolean;
  /** View/preview action in flight — spins whichever button triggers the view. */
  isViewing?: boolean;
  onDownload: () => void;
  onView?: () => void;
  onShare?: () => void;
  showShare?: boolean;
  showView?: boolean;
  passwordText?: string;
  showPassword?: boolean;
  statusLabel?: string;
  statusColor?: string;
  statusDescription?: string;
  disabledHint?: string;
  primaryAction?: "download" | "view";
  fileType?: "pdf" | "zip";
  sizeText?: string;
  metaText?: string;
}

const STATUS_VARIANT_MAP: Record<
  string,
  "success" | "warning" | "info" | "pending" | "neutral"
> = {
  success: "success",
  warning: "warning",
  processing: "pending",
  default: "neutral",
};

const FILE_ICON_STYLES: Record<"pdf" | "zip", string> = {
  pdf: "bg-gradient-to-b from-red-100 to-red-200 text-red-700",
  zip: "bg-gradient-to-b from-violet-100 to-violet-200 text-violet-700",
};

const DownloadItem: React.FC<DownloadItemProps> = ({
  title,
  isCompleted,
  isDownloading = false,
  isViewing = false,
  onDownload,
  onView,
  onShare,
  showShare = false,
  showView = true,
  passwordText = "",
  showPassword = false,
  statusLabel,
  statusColor = "default",
  statusDescription,
  disabledHint,
  primaryAction = "download",
  fileType = "pdf",
  sizeText,
  metaText,
}) => {
  const [copied, setCopied] = useState(false);

  const handlePasswordCopy = async () => {
    if (!passwordText || !isCompleted) {
      return;
    }

    try {
      await navigator.clipboard.writeText(passwordText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Error copying password:", error);
    }
  };

  const variant = STATUS_VARIANT_MAP[statusColor] ?? "neutral";
  const tooltipForDisabled = (label: string) =>
    !isCompleted && disabledHint ? disabledHint : label;

  const primaryIsView = primaryAction === "view" && showView && !!onView;
  const primaryLoading = primaryIsView ? isViewing : isDownloading;

  return (
    <div
      className={cn(
        "group flex items-center gap-3 p-3 transition-colors hover:bg-muted/40",
        !isCompleted && "opacity-65",
      )}
    >
      {/* File icon */}
      <div
        className={cn(
          "relative flex size-[38px] shrink-0 items-center justify-center rounded-lg font-bold text-[9px] tracking-wider",
          FILE_ICON_STYLES[fileType],
        )}
        aria-hidden="true"
      >
        {fileType.toUpperCase()}
        {showPassword && passwordText && (
          <span
            title="Protegido con contraseña"
            className="absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full border-2 border-white bg-gray-800 text-white"
          >
            <Lock className="size-2" strokeWidth={3} />
          </span>
        )}
      </div>

      {/* Info column */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight text-foreground">
          {title}
          {showPassword && passwordText && (
            <span className="sr-only"> (protegido con contraseña)</span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          {statusLabel && (
            <StatusBadge
              status={variant}
              label={statusLabel}
              showDot
              className="border-0 bg-transparent px-0 py-0"
            />
          )}
          {sizeText && (
            <>
              <span className="text-zinc-300" aria-hidden="true">·</span>
              <span>{sizeText}</span>
            </>
          )}
          {metaText && (
            <>
              <span className="text-zinc-300" aria-hidden="true">·</span>
              <span>{metaText}</span>
            </>
          )}
          {statusDescription && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help">
                  <InfoIcon className="size-3 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[240px]">
                <p className="text-xs leading-relaxed">{statusDescription}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Secondary actions — hidden by default, shown on hover */}
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          {/* Copy password */}
          {showPassword && passwordText && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={handlePasswordCopy}
                    disabled={!isCompleted}
                    aria-label={copied ? "Contraseña copiada" : "Copiar contraseña"}
                  >
                    {copied ? (
                      <CheckIcon className="size-4 text-emerald-500" />
                    ) : (
                      <CopyIcon className="size-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {copied ? "Contraseña copiada" : tooltipForDisabled("Copiar contraseña")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* View — secondary when download is primary */}
          {!primaryIsView && showView && onView && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={onView}
                    disabled={!isCompleted || isViewing}
                    aria-label={isViewing ? "Abriendo…" : "Ver"}
                  >
                    {isViewing ? (
                      <Loader2Icon className="size-4 animate-spin" />
                    ) : (
                      <EyeIcon className="size-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{tooltipForDisabled("Ver")}</TooltipContent>
            </Tooltip>
          )}

          {/* Download — secondary when view is primary */}
          {primaryIsView && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={onDownload}
                    disabled={!isCompleted || isDownloading}
                    aria-label={isDownloading ? "Descargando…" : "Descargar"}
                  >
                    {isDownloading ? (
                      <Loader2Icon className="size-4 animate-spin" />
                    ) : (
                      <DownloadIcon className="size-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{tooltipForDisabled("Descargar")}</TooltipContent>
            </Tooltip>
          )}

          {/* Share */}
          {showShare && onShare && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={onShare}
                    disabled={!isCompleted}
                    aria-label="Compartir"
                  >
                    <Share2Icon className="size-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{tooltipForDisabled("Compartir")}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Primary action — always visible */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="default"
                size="icon"
                className="size-8"
                onClick={primaryIsView ? onView : onDownload}
                disabled={!isCompleted || primaryLoading}
                aria-label={
                  primaryLoading
                    ? primaryIsView
                      ? "Abriendo…"
                      : "Descargando…"
                    : primaryIsView
                      ? "Ver"
                      : "Descargar"
                }
              >
                {primaryLoading ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : primaryIsView ? (
                  <EyeIcon className="size-4" />
                ) : (
                  <DownloadIcon className="size-4" />
                )}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {tooltipForDisabled(primaryIsView ? "Ver" : "Descargar")}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default DownloadItem;
